import * as queryString from 'query-string'
import type { ValuesType } from 'utility-types'
import {
  APPLICATION_FIELD_QUERY_PARAM_PREFIX,
  ConfigKeys,
  Filter,
  FILTER_QUERY_PARAM_PREFIX,
  QueryParamKeys,
} from '@evenfinancial/even-ts-static'

export const REDACTED_VALUE = '[REDACTED]'

type WithAppPrefix<T extends string> = `${typeof APPLICATION_FIELD_QUERY_PARAM_PREFIX}${T}`
type WithFilterPrefix<T extends string> = `${typeof FILTER_QUERY_PARAM_PREFIX}${T}`
type BasePossiblePiiKeyType = ValuesType<typeof ConfigKeys> | Filter

export type PossiblePIIKeyType =
  | BasePossiblePiiKeyType
  | WithAppPrefix<BasePossiblePiiKeyType>
  | WithFilterPrefix<BasePossiblePiiKeyType>

let APP_PII_FIELDS: Set<WithAppPrefix<BasePossiblePiiKeyType>>
let FILTER_PII_FIELDS: Set<WithFilterPrefix<BasePossiblePiiKeyType>>
let PII_FIELDS: Set<PossiblePIIKeyType>

function getPIIFieldsSet(): typeof PII_FIELDS {
  if (!PII_FIELDS) {
    PII_FIELDS = new Set<PossiblePIIKeyType>([
      ConfigKeys.AddressOne,
      ConfigKeys.AddressTwo,
      ConfigKeys.City,
      ConfigKeys.DateOfBirth,
      ConfigKeys.Email,
      ConfigKeys.FirstName,
      ConfigKeys.LastName,
      ConfigKeys.State,
      ConfigKeys.Zipcode,
      Filter.AddressOne,
      Filter.AddressTwo,
      Filter.BankRoutingNumber,
      Filter.City,
      Filter.CoApplicantInformation,
      Filter.CoApplicantAddressMatch,
      Filter.CoApplicantAddressOne,
      Filter.CoApplicantAddressTwo,
      Filter.CoApplicantDateOfBirth,
      Filter.CoApplicantFirstName,
      Filter.CoApplicantLastName,
      Filter.CoApplicantState,
      Filter.CoApplicantCity,
      Filter.CoApplicantZipcode,
      Filter.DateOfBirth,
      Filter.Email,
      Filter.FirstName,
      Filter.LastName,
      Filter.Password,
      Filter.PropertyAddressOne,
      Filter.PropertyAddressTwo,
      Filter.PropertyCity,
      Filter.PropertyState,
      Filter.PropertyZipcode,
      Filter.PrimaryPhone,
      Filter.SocialSecurityNumber,
      Filter.State,
      Filter.StreetAddressOne,
      Filter.StreetAddressTwo,
      Filter.VehicleIdentificationNumber,
      Filter.Zipcode,
      QueryParamKeys.AddressOne,
      QueryParamKeys.AddressTwo,
      QueryParamKeys.City,
      QueryParamKeys.DateOfBirth,
      QueryParamKeys.Email,
      QueryParamKeys.FirstName,
      QueryParamKeys.LastName,
      QueryParamKeys.State,
      QueryParamKeys.Zipcode,
    ])
    for (const field of PII_FIELDS) {
      if (!(field.startsWith(APPLICATION_FIELD_QUERY_PARAM_PREFIX) || field.startsWith(FILTER_QUERY_PARAM_PREFIX))) {
        PII_FIELDS.add(`${APPLICATION_FIELD_QUERY_PARAM_PREFIX}${field as BasePossiblePiiKeyType}`)
        PII_FIELDS.add(`${FILTER_QUERY_PARAM_PREFIX}${field as BasePossiblePiiKeyType}`)
      }
    }
  }
  return PII_FIELDS
}

function getAppPIIFieldsSet(): typeof APP_PII_FIELDS {
  if (!APP_PII_FIELDS) {
    APP_PII_FIELDS = new Set()
    for (const field of getPIIFieldsSet()) {
      if (field.startsWith(APPLICATION_FIELD_QUERY_PARAM_PREFIX)) {
        APP_PII_FIELDS.add(field as WithAppPrefix<BasePossiblePiiKeyType>)
      }
    }
  }
  return APP_PII_FIELDS
}

function getFilterPIIFieldsSet(): typeof FILTER_PII_FIELDS {
  if (!FILTER_PII_FIELDS) {
    FILTER_PII_FIELDS = new Set()
    for (const field of getPIIFieldsSet()) {
      if (field.startsWith(FILTER_QUERY_PARAM_PREFIX)) {
        FILTER_PII_FIELDS.add(field as WithFilterPrefix<BasePossiblePiiKeyType>)
      }
    }
  }
  return FILTER_PII_FIELDS
}

export function fieldIsPii(fieldName: PossiblePIIKeyType | string): boolean {
  return getPIIFieldsSet().has(fieldName as PossiblePIIKeyType)
}

export function sanitizeFieldsForTracking<T extends Object>(obj: T): T {
  const sanitizedObj = {}

  for (const [key, value] of Object.entries(obj)) {
    switch (typeof value) {
      case 'string':
      case 'number':
        sanitizedObj[key] = fieldIsPii(key) ? REDACTED_VALUE : value
        break
      default:
        sanitizedObj[key] = value
    }
  }

  return sanitizedObj as T
}

export function urlContainsPossiblyBlacklistedPiiParams(url?: unknown): boolean {
  return (
    typeof url === 'string' &&
    (url.includes(`&${APPLICATION_FIELD_QUERY_PARAM_PREFIX}`) ||
      url.includes(`?${APPLICATION_FIELD_QUERY_PARAM_PREFIX}`) ||
      url.includes(`&${FILTER_QUERY_PARAM_PREFIX}`) ||
      url.includes(`?${FILTER_QUERY_PARAM_PREFIX}`))
  )
}

export function redactUrlParamsWithPossiblePii(url: string): string {
  const parsedUrl = queryString.parseUrl(url)
  if (parsedUrl.query) {
    const appPIIFieldsSet = getAppPIIFieldsSet()
    const filterPIIFieldsSet = getFilterPIIFieldsSet()
    const filteredParsedQueryObject = Object.entries(parsedUrl.query).reduce((redactedQueryObject, [key, value]) => {
      redactedQueryObject[key] =
        key === 'app' ||
        appPIIFieldsSet.has(key as WithAppPrefix<BasePossiblePiiKeyType>) ||
        filterPIIFieldsSet.has(key as WithFilterPrefix<BasePossiblePiiKeyType>)
          ? REDACTED_VALUE
          : value
      return redactedQueryObject
    }, {})
    return `${parsedUrl.url}?${queryString.stringify(filteredParsedQueryObject)}`
  }
  return url
}
